import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Shield, AlertCircle, Loader2, RefreshCw, User, Mail, Phone, Lock, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNOPQRSTUVWXYZabcdefghjkmnopqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    setCaptchaVerified(false);
    setCaptchaInput('');
  };

  const verifyCaptcha = () => {
    if (captchaInput === captchaText) {
      setCaptchaVerified(true);
      toast.success('CAPTCHA verified');
      return true;
    } else {
      toast.error('Invalid CAPTCHA');
      generateCaptcha();
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Only check CAPTCHA for Sign Up, not for Login
    if (mode === 'signup' && !captchaVerified) {
      setError('Please verify CAPTCHA first');
      return;
    }

    setLoading(true);
    
    try {
      let response;
      if (mode === 'signup') {
        response = await axios.post(`${API_URL}/api/auth/signup`, {
          phone,
          name,
          email,
          password
        });
      } else {
        response = await axios.post(`${API_URL}/api/auth/login`, {
          phone,
          password
        });
      }
      
      // Save token and trigger user fetch
      localStorage.setItem('token', response.data.token);
      
      // Force a page reload to ensure AuthContext picks up the new token
      toast.success(mode === 'signup' ? 'Account created successfully!' : 'Login successful!');
      
      // Use window.location instead of navigate to force full page reload
      window.location.href = from;
      
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${mode === 'signup' ? 'sign up' : 'login'}`);
      toast.error(err.response?.data?.detail || 'Authentication failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center px-4 py-12" data-testid="login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-primary-600" />
          </div>
          <CardTitle className="text-2xl font-bold font-heading text-secondary-900">
            Welcome to Noble Shield
          </CardTitle>
          <CardDescription>
            {mode === 'signup' ? 'Create your account' : 'Login to your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6">
            <Button type="button" variant={mode === 'login' ? 'default' : 'outline'} className="flex-1" onClick={() => { setMode('login'); setError(''); }} data-testid="login-tab">Login</Button>
            <Button type="button" variant={mode === 'signup' ? 'default' : 'outline'} className="flex-1" onClick={() => { setMode('signup'); setError(''); }} data-testid="signup-tab">Sign Up</Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4" data-testid="error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name"><User className="inline h-4 w-4 mr-1" />Full Name *</Label>
                  <Input id="name" type="text" placeholder="Enter your full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="name-input" disabled={loading} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email"><Mail className="inline h-4 w-4 mr-1" />Email Address *</Label>
                  <Input id="email" type="email" placeholder="your.email@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input" disabled={loading} required />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="phone"><Phone className="inline h-4 w-4 mr-1" />Phone Number *</Label>
              <Input id="phone" type="tel" placeholder="Enter 10-digit phone number" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} data-testid="phone-input" disabled={loading} required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password"><Lock className="inline h-4 w-4 mr-1" />Password *</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder={mode === 'signup' ? 'Create a password (min 6 chars)' : 'Enter your password'} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="password-input" disabled={loading} required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-500 hover:text-secondary-700">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* CAPTCHA Section - Only for Sign Up */}
            {mode === 'signup' && (
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <Label className="mb-2 block">Verify CAPTCHA *</Label>
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <div className="bg-white p-3 rounded border-2 border-dashed border-secondary-300 text-center font-mono text-xl tracking-widest text-secondary-900" style={{ fontFamily: 'cursive' }}>{captchaText}</div>
                  </div>
                  <div className="col-span-2">
                    <Button type="button" variant="ghost" size="icon" onClick={generateCaptcha} title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
                  </div>
                  <div className="col-span-6">
                    <Input placeholder="Enter CAPTCHA" value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value)} disabled={captchaVerified || loading} onBlur={verifyCaptcha} data-testid="captcha-input" />
                  </div>
                </div>
                {captchaVerified && <p className="text-xs text-green-600 mt-2">✓ Verified</p>}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading || phone.length !== 10 || !password || (mode === 'signup' && (!name || !email || !captchaVerified))} data-testid="submit-button">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{mode === 'signup' ? 'Creating Account...' : 'Logging In...'}</> : mode === 'signup' ? 'Sign Up' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
